import React, { ChangeEvent, FC, Fragment, useCallback, useContext, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { Button, Col, Input, Row, Select } from 'antd';
import * as Icons from '@ant-design/icons';

import { AntForm } from '@core/ui';
import { CheckboxField, SelectField, TextAreaField, TextField } from '@core/form';
import { useCreateCountry } from '../hooks';
import { CreateCountryDto } from '../dtos';
import { useStatusByModelId } from '@modules/status';
import { CreateCountryContext } from '../contexts';

const FormikComponent: FC<FormikProps<CreateCountryDto>> = ({ handleSubmit, isSubmitting, setFieldValue, values, setValues }) => {
  const { id } = useContext(CreateCountryContext);
  const [newFieldName, setNewFieldName] = useState<string>('');
  const [newFieldValue, setNewFieldValue] = useState<string>('');

  const onFieldNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNewFieldName(e.target.value);
  }, []);

  const onFieldValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNewFieldValue(e.target.value);
  }, []);

  const onKeyChange = useCallback(
    (key: string, e: ChangeEvent<HTMLInputElement>) => {
      const copy = {};
      Object.keys(values.customFields).forEach((k) => {
        if (k === key) {
          copy[e.target.value] = values.customFields[k];
        } else {
          copy[k] = values.customFields[k];
        }
        setFieldValue('customFields', copy);
      });
    },
    [setFieldValue, values],
  );

  const onDeleteKey = useCallback(
    (key: string) => {
      const copy = { ...values };
      delete copy['customFields'][key];
      setValues(copy);
    },
    [values, setValues],
  );

  const handleAddNewPair = useCallback(() => {
    setFieldValue(`customFields.${newFieldName}`, newFieldValue);
    setNewFieldName('');
    setNewFieldValue('');
  }, [newFieldName, newFieldValue, setFieldValue]);

  const states = useStatusByModelId('39');
  return (
    <AntForm layout='vertical' style={{ marginBottom: 0 }} component='div' size='large'>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <TextField name='countryName' item={{ label: 'Ad' }} input={{ placeholder: 'Ölkə adını daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='abbr' item={{ label: 'Qısaltma' }} input={{ placeholder: 'Qısaltmanı daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='countryCode' item={{ label: 'Ölkə kodu' }} input={{ placeholder: 'Ölkə kodunu daxil edin...' }} />
        </Col>
      </Row>
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={8}>
          <TextField name='currency' item={{ label: 'Valyuta adı' }} input={{ placeholder: 'Valyuta adını daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='currencyType' item={{ label: 'Valyuta kodu' }} input={{ placeholder: 'Valyuta kodunu daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <SelectField name='unit' item={{ label: 'Ölçü' }} input={{ placeholder: 'Ölçü seçin...' }}>
            <Select.Option value={'LBS'}>LBS</Select.Option>
            <Select.Option value={'KG'}>KG</Select.Option>
          </SelectField>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <TextField name='carrierCompanyName' item={{ label: 'Daşıyıcı şirkət adı' }} input={{ placeholder: 'Adı daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='carrierCompanyAddress' item={{ label: 'Daşıyıcı şirkət ünvanı' }} input={{ placeholder: 'Ünvanı daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='carrierCompanyPhone' item={{ label: 'Daşıyıcı şirkət mobil nömrəsi' }} input={{ placeholder: 'Daşıyıcı şirkət mobil nömrəsi daxil edin...' }} />
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <SelectField name='stateId' item={{ label: 'Status' }} input={{ placeholder: 'Status seçin...' }}>
            {states.data?.map((state) => (
              <Select.Option key={state.id} value={state.id.toString()}>
                {state.name}
              </Select.Option>
            ))}
          </SelectField>
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='address' item={{ label: 'Adres' }} input={{ placeholder: 'Adresi daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='addressHeading' item={{ label: 'Adres başlığı' }} input={{ placeholder: 'Adres başlığını daxil edin...' }} />
        </Col>
        <Col xs={24} lg={8}>
          <TextField name='minSize' item={{ label: 'Min. həcm ölçüsü' }} input={{ placeholder: 'Min. həcm ölçüsünü daxil edin...' }} />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxField name='box' input={{ children: 'Kisə filial bağlılığı' }} />
        </Col>
        <Col>
          <CheckboxField name='isOrder' input={{ children: 'Sifariş et xidməti' }} />
        </Col>
        <Col>
          <CheckboxField name='isDeclaration' input={{ children: 'Bəyan etmək' }} />
        </Col>
        <Col>
          <CheckboxField name='smsConfirmation' input={{ children: 'SMS təsdiq nömrəsi' }} />
        </Col>
        <Col>
          <CheckboxField name='fullDeclaration' input={{ children: 'Bağlama məlumatları tam doldurulmalı' }} />
        </Col>
        <Col>
          <CheckboxField name='zeroPrice' input={{ children: 'Bağlama qiyməti 0 ola bilər?' }} />
        </Col>
        <Col>
          <CheckboxField name='zeroSend' input={{ children: 'Qiymət 0 DGK göndərilsin?' }} />
        </Col>
        <Col>
          <CheckboxField name='hasWarehouse' input={{ children: 'Xarici anbarı var?' }} />
        </Col>
      </Row>
      <TextAreaField name='descr' item={{ label: 'Ətraflı' }} input={{ placeholder: 'Ətraflı daxil edin...' }} />
      <hr />
      <h3>Sərbəst sahələr</h3>
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={10}>
          <Input onChange={onFieldNameChange} value={newFieldName} placeholder='Key Daxil et' />
        </Col>
        <Col xs={24} lg={10}>
          <Input onChange={onFieldValueChange} value={newFieldValue} placeholder='Value Daxil et' />
        </Col>
        <Col xs={24} lg={4}>
          <Button onClick={handleAddNewPair} style={{ width: '100%' }}>
            Əlavə et
          </Button>
        </Col>
        {Object.keys(values.customFields).map((key, index) => (
          <Fragment key={index}>
            <Col xs={24} lg={10}>
              <Input value={key} onChange={(e) => onKeyChange(key, e)} placeholder='Başlıq daxil et' />
            </Col>
            <Col xs={24} lg={10}>
              <TextField name={`customFields.${key}`} input={{ placeholder: 'Qiymət daxil et' }} />
            </Col>
            <Col xs={24} lg={4}>
              <Button onClick={() => onDeleteKey(key)} style={{ width: '100%' }}>
                <Icons.DeleteFilled />
              </Button>
            </Col>
          </Fragment>
        ))}
      </Row>
      <br />
      <Button onClick={() => handleSubmit()} disabled={isSubmitting} type='primary' style={{ width: '100%' }} block={true} loading={isSubmitting}>
        {id ? 'Dəyiş' : 'Yarat'}
      </Button>
    </AntForm>
  );
};

export const CreateCountry: FC = () => {
  const { initialValues, onSubmit } = useCreateCountry();
  if (!initialValues) {
    return null;
  }
  return <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true} component={FormikComponent} />;
};
